import { useState, useEffect } from 'react';

/**
 * Custom hook for Growth Equation metrics
 * Tracks the progression: Strangers → Visitors → Leads → Buyers → LTV
 */
export const useGrowthEquation = (dateRange) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));

      // Mock data based on the image
      const mockData = {
        funnel: {
          strangers: 25000,
          visitors: 15000,
          leads: 195, // MQLs
          sqls: 20, // Booked calls
          buyers: 12, // Closed deals
        },
        conversionRates: {
          strangersToVisitors: 0.60, // 60%
          visitorsToLeads: 0.013, // 1.3%
          leadsToSQLs: 0.10, // 10%
          sqlsToBuyers: 0.60, // 60% show up rate
          closingRate: 0.25, // 25%
        },
        metrics: {
          ltv: 45000, // Lifetime value
          avgSale: 12000, // Average sale value
          cpa: 2500, // Cost per acquisition
          roas: 4.8, // Return on ad spend (4.8x)
          costPerLead: 250,
          costPerSQL: 1200,
        },
        monthlyBreakdown: [
          { month: 'Jan', strangers: 2100, visitors: 1250, leads: 16, sqls: 2, buyers: 1 },
          { month: 'Feb', strangers: 2000, visitors: 1200, leads: 15, sqls: 2, buyers: 1 },
          { month: 'Mar', strangers: 2200, visitors: 1350, leads: 18, sqls: 2, buyers: 1 },
          { month: 'Apr', strangers: 2050, visitors: 1230, leads: 16, sqls: 2, buyers: 1 },
          { month: 'May', strangers: 2150, visitors: 1280, leads: 17, sqls: 2, buyers: 1 },
          { month: 'Jun', strangers: 2100, visitors: 1260, leads: 16, sqls: 2, buyers: 1 },
          { month: 'Jul', strangers: 2000, visitors: 1200, leads: 15, sqls: 1, buyers: 1 },
          { month: 'Aug', strangers: 2050, visitors: 1230, leads: 16, sqls: 2, buyers: 1 },
          { month: 'Sep', syngers: 2100, visitors: 1250, leads: 16, sqls: 2, buyers: 1 },
          { month: 'Oct', strangers: 2150, visitors: 1290, leads: 17, sqls: 2, buyers: 1 },
          { month: 'Nov', strangers: 2200, visitors: 1320, leads: 17, sqls: 2, buyers: 1 },
          { month: 'Dec', strangers: 1900, visitors: 1140, leads: 15, sqls: 1, buyers: 1 },
        ],
      };

      setData(mockData);
    } catch (err) {
      setError(err.message || 'Failed to fetch growth equation data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const refetch = () => fetchData(true);

  return {
    data,
    loading,
    refreshing,
    error,
    refetch,
  };
};